import React, { useState, useEffect } from 'react';
import auctionService from '../services/auctionService';
import { formatCurrency, calculatePercentage } from '../utils/validators';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './Analytics.css';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [analyticsRes, perfRes] = await Promise.all([
        auctionService.getAnalytics(),
        auctionService.getPerformance(),
      ]);

      if (analyticsRes.success) {
        setAnalytics(analyticsRes.data);
      }
      if (perfRes.success) {
        setPerformance(perfRes.data);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="container py-4"><div className="spinner"><div className="loading"></div>Loading...</div></div>;
  }

  const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#d97706'];

  return (
    <div className="container py-4">
      <h1>Seller Analytics</h1>

      {/* KPI Cards */}
      <div className="grid grid-4">
        <div className="stat-card">
          <div className="stat-label">Total Sales</div>
          <div className="stat-value">{formatCurrency(analytics?.totalSales || 0)}</div>
          <div className="stat-subtitle">This month</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Sale Price</div>
          <div className="stat-value">{formatCurrency(analytics?.avgPrice || 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Sell-Through Rate</div>
          <div className="stat-value">{analytics?.sellThroughRate || 0}%</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Time to Sell</div>
          <div className="stat-value">{analytics?.avgDaysToSell || 0} days</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-2 mt-3">
        {/* Sales Trend */}
        <div className="card">
          <h3>Sales Trend (30 days)</h3>
          {analytics?.chartData && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sales" stroke="#2563eb" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Auction Status Distribution */}
        <div className="card">
          <h3>Auction Status</h3>
          {performance && (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Completed', value: performance.completedCount },
                    { name: 'Active', value: performance.activeCount },
                    { name: 'Paused', value: performance.pausedCount },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {COLORS.map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-2 mt-3">
        {/* Price Distribution */}
        <div className="card">
          <h3>Price Range Distribution</h3>
          {analytics?.priceDistribution && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.priceDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Vehicle Categories */}
        <div className="card">
          <h3>Top Vehicle Categories</h3>
          {analytics?.topCategories && (
            <div className="category-list">
              {analytics.topCategories.map((category, idx) => (
                <div key={idx} className="category-item">
                  <div className="category-name">{category.name}</div>
                  <div className="category-bar">
                    <div
                      className="category-progress"
                      style={{
                        width: `${calculatePercentage(category.count, analytics.topCategories[0].count)}%`,
                      }}
                    ></div>
                  </div>
                  <div className="category-count">{category.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="card mt-3">
        <h3>Performance Metrics</h3>
        <div className="metrics-grid">
          {performance && (
            <>
              <div className="metric">
                <span>Active Auctions</span>
                <strong>{performance.activeCount}</strong>
              </div>
              <div className="metric">
                <span>Completed Auctions</span>
                <strong>{performance.completedCount}</strong>
              </div>
              <div className="metric">
                <span>Paused Auctions</span>
                <strong>{performance.pausedCount}</strong>
              </div>
              <div className="metric">
                <span>Avg Bid Count</span>
                <strong>{performance.avgBidCount}</strong>
              </div>
              <div className="metric">
                <span>Avg Bid Value</span>
                <strong>{formatCurrency(performance.avgBidValue || 0)}</strong>
              </div>
              <div className="metric">
                <span>Buyer Feedback Rating</span>
                <strong>{performance.feedbackRating || 'N/A'}</strong>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
